import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';

// --- TypeScript типы для SEO-конфигурации ---

export interface District {
  readonly slug: string;
  readonly name: string;
  readonly deliveryTime: string;
}

export interface CityConfig {
  readonly name: string;
  readonly nameGenitive: string;
  readonly nameLocative: string;
  readonly region: string;
  readonly coordinates: { readonly lat: number; readonly lng: number };
  readonly districts: readonly District[];
}

export interface VerificationConfig {
  readonly yandex: string;
  readonly google: string;
}

export interface SeoConfig {
  readonly defaultKeywords: readonly string[];
}

// --- Конфигурация сайта ---

export const siteConfig = {
  name: 'Азиаточка',
  description: 'Доставка роллов и суши в Ростове-на-Дону',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://aziatochka.ru',
  ogImage: '/opengraph-image',

  city: {
    name: 'Ростов-на-Дону',
    nameGenitive: 'Ростове-на-Дону',
    nameLocative: 'Ростов-на-Дону',
    region: 'RU-ROS',
    coordinates: { lat: 47.2357, lng: 39.7015 },
    districts: [
      { slug: 'voroshilovskiy', name: 'Ворошиловский район', deliveryTime: '45-60 мин' },
      { slug: 'leninskiy', name: 'Ленинский район', deliveryTime: '40-55 мин' },
      { slug: 'sovetskiy', name: 'Советский район', deliveryTime: '50-70 мин' },
      { slug: 'pervomayskiy', name: 'Первомайский район', deliveryTime: '45-65 мин' },
      { slug: 'oktyabrskiy', name: 'Октябрьский район', deliveryTime: '50-70 мин' },
      { slug: 'proletarskiy', name: 'Пролетарский район', deliveryTime: '55-75 мин' },
      { slug: 'kirovskiy', name: 'Кировский район', deliveryTime: '50-70 мин' },
      { slug: 'zheleznodorozhniy', name: 'Железнодорожный район', deliveryTime: '45-65 мин' },
    ],
  },

  // Телефон колл-центра. Отсюда он уходит в Schema.org (lib/seo/json-ld.ts:
  // Restaurant.telephone и LocalBusiness.telephone), то есть публикуется в
  // Google. До 16.08.2026 здесь стоял «+7 (863) 333-33-33» — несуществующий
  // номер: настоящий 333-50-50 стоит в FAQ, футере, шапке и ещё в двух
  // десятках файлов. Владелец подтвердил (вопрос 48), что 333-50-50 верный,
  // а 333-33-33 был опечаткой.
  //
  // Почему опечатка прожила так долго при зелёных тестах: json-ld.test.ts
  // работал на собственной фикстуре и этот файл не импортировал — то есть
  // ничего здесь не проверял. Теперь в конце того файла есть отдельный блок,
  // который берёт настоящий siteConfig и сверяет телефон и часы.
  phone: '+7 (863) 333-50-50',
  email: 'info@aziatochka.ru',
  address: 'г. Ростов-на-Дону',

  // Часы приёма заказов: 11:00–22:00 (решение владельца по вопросу 46).
  // Значение НЕ дублируется, а берётся из lib/business-hours.ts — там же
  // живёт логика «открыто/закрыто» и параметры слотов доставки. Раньше эти
  // числа стояли в обоих файлах независимо, и разъехаться им ничего не мешало.
  workingHours: {
    start: OPEN_HOUR,
    end: CLOSE_HOUR,
  },

  links: {
    vk: 'https://vk.com/aziatochka_ru',
    instagram: 'https://instagram.com/aziatochka_rnd',
    // Площадки для отзывов по акции «Вы нам отзыв, мы вам ролл». Владелец меняет
    // их в админке (настройки links.review_*), здесь — запасные адреса на случай,
    // если настройки не ответили. См. lib/review-platforms.ts.
    reviews: {
      yandex: 'https://yandex.ru/profile/206598773091',
      gis: 'https://2gis.ru/rostov-on-don/firm/70000001100689913',
      vk: 'https://vk.com/aziatochka_ru',
    },
  },

  verification: {
    yandex: ['64840d325eb42fc5', '7282d6c5dbf7529e'],
    google: '6e_S3oiXOgP8tOFDkKiJezWO2OZtnESrNJleRkV6QYc',
  },

  seo: {
    defaultKeywords: [
      // ═══════════════════════════════════════════════════════════════
      // 1. БРЕНДОВЫЕ
      // ═══════════════════════════════════════════════════════════════
      'Азиаточка',
      'азиаточка роллы',
      'азиаточка ростов',
      'азиаточка доставка',
      'азиаточка ростов на дону',
      'азиаточка меню',
      'азиаточка отзывы',
      'азиаточка промокод',
      'азиаточка акции',
      'азиаточка сайт',
      'азиаточка приложение',
      'aziatochka',
      'aziatochka.ru',

      // ═══════════════════════════════════════════════════════════════
      // 2. ВЫСОКОЧАСТОТНЫЕ КОММЕРЧЕСКИЕ (generateKeywords добавит гео)
      // ═══════════════════════════════════════════════════════════════
      'доставка роллов',
      'доставка суши',
      'заказать роллы',
      'заказать суши',
      'роллы с доставкой',
      'суши с доставкой',
      'роллы на дом',
      'суши на дом',
      'роллы на заказ',
      'суши на заказ',
      'заказ роллов',
      'заказ суши',
      'купить роллы',
      'купить суши',
      'роллы онлайн',
      'суши онлайн',
      'заказать роллы онлайн',
      'заказать суши онлайн',
      'роллы доставка на дом',
      'суши доставка на дом',
      'роллы заказать с доставкой',
      'суши заказать с доставкой',
      'доставка роллов на дом',
      'доставка суши на дом',
      'роллы быстрая доставка',
      'суши быстрая доставка',

      // ═══════════════════════════════════════════════════════════════
      // 3. ГЕО-ПРИВЯЗКА — Ростов / РнД / Ростов-на-Дону
      // ═══════════════════════════════════════════════════════════════
      'доставка роллов ростов',
      'доставка роллов ростов на дону',
      'доставка суши ростов на дону',
      'доставка суши ростов',
      'роллы ростов',
      'суши ростов',
      'роллы ростов на дону',
      'суши ростов на дону',
      'заказать роллы ростов на дону',
      'заказать суши ростов на дону',
      'роллы с доставкой ростов',
      'суши с доставкой ростов',
      'роллы на дом ростов',
      'суши на дом ростов',
      'роллы рнд',
      'суши рнд',
      'доставка роллов рнд',
      'доставка суши рнд',
      'заказать роллы рнд',
      'заказать суши рнд',
      'роллы ростов на дону доставка',
      'суши ростов на дону доставка',
      'роллы ростов на дону заказать',
      'суши ростов на дону заказать',
      'роллы ростов на дону меню',
      'суши ростов на дону меню',

      // ═══════════════════════════════════════════════════════════════
      // 4. КАТЕГОРИЙНЫЕ — конкретные виды роллов
      // ═══════════════════════════════════════════════════════════════
      // Филадельфия
      'филадельфия ростов',
      'филадельфия с доставкой',
      'ролл филадельфия заказать',
      'филадельфия ростов на дону',
      'филадельфия цена',
      'филадельфия ролл доставка',
      'филадельфия классик',
      'филадельфия лайт',
      // Запечённые
      'запечённые роллы',
      'запечённые роллы ростов',
      'запечённые роллы с доставкой',
      'запечённые роллы заказать',
      'запечённые роллы ростов на дону',
      'запечённые роллы недорого',
      // Горячие
      'горячие роллы',
      'горячие роллы ростов',
      'горячие роллы с доставкой',
      'горячие роллы заказать',
      'горячие роллы ростов на дону',
      // Холодные
      'холодные роллы',
      'холодные роллы ростов',
      'холодные роллы с доставкой',
      'холодные роллы заказать',
      // Темпура
      'темпура роллы',
      'темпура ростов',
      'темпура с доставкой',
      'темпура роллы заказать',
      'темпура ростов на дону',
      // Сеты
      'сеты роллов',
      'сеты роллов ростов',
      'сеты суши',
      'сеты суши ростов',
      'набор роллов',
      'набор роллов с доставкой',
      'набор суши с доставкой',
      'сет роллов с доставкой',
      'сет суши с доставкой',
      'сеты роллов ростов на дону',
      'большой сет роллов',
      'сет роллов на компанию',
      'сет роллов недорого',
      'сет роллов на двоих',
      'сет роллов на четверых',
      // Конкретные роллы
      'сашими ростов',
      'маки роллы',
      'маки роллы ростов',
      'калифорния ролл',
      'калифорния ролл ростов',
      'калифорния с доставкой',
      'ролл с лососем',
      'ролл с лососем ростов',
      'ролл с креветкой',
      'ролл с креветкой ростов',
      'ролл с угрём',
      'ролл с угрём ростов',
      'ролл с тунцом',
      'ролл с тунцом ростов',
      'спайси роллы',
      'спайси роллы ростов',
      'спайси с доставкой',
      'вегетарианские роллы',
      'вегетарианские роллы ростов',
      'ролл дракон',
      'ролл дракон ростов',
      'ролл цезарь',
      'ролл с сыром',
      'ролл с авокадо',
      'ролл с огурцом',
      'ролл с крабом',
      'ролл с курицей',
      'ролл с беконом',
      'ролл унаги',
      'ролл канада',
      'ролл аляска',
      'ролл бонито',
      'ролл с икрой',
      'ролл тобико',
      'ролл с масаго',
      'радужный ролл',
      'фирменные роллы',
      'авторские роллы',
      'авторские роллы ростов',
      'премиум роллы',
      'премиум роллы ростов',
      'мини роллы',
      'мини роллы ростов',

      // ═══════════════════════════════════════════════════════════════
      // 5. ДОПОЛНИТЕЛЬНЫЕ БЛЮДА МЕНЮ
      // ═══════════════════════════════════════════════════════════════
      // Поке
      'поке ростов',
      'поке с доставкой',
      'поке с доставкой ростов',
      'поке боул ростов',
      'поке заказать',
      // Рамен / лапша
      'рамен ростов',
      'рамен с доставкой',
      'рамен заказать ростов',
      'лапша ростов',
      'лапша с доставкой ростов',
      'лапша удон ростов',
      'лапша соба ростов',
      'фунчоза ростов',
      'фунчоза с доставкой',
      // Вок
      'wok ростов',
      'вок ростов',
      'вок с доставкой ростов',
      'вок заказать ростов',
      'вок лапша ростов',
      'вок с курицей ростов',
      'вок с креветками ростов',
      'вок с овощами ростов',
      // Мисо суп / супы
      'мисо суп ростов',
      'мисо суп с доставкой',
      'мисо суп заказать',
      'японские супы ростов',
      'том ям ростов',
      'том ям с доставкой',
      // Гунканы
      'гунканы ростов',
      'гунканы с доставкой',
      'гунканы заказать',
      // Нигири
      'нигири ростов',
      'нигири с доставкой',
      'нигири суши заказать',
      // Онигири
      'онигири ростов',
      'онигири с доставкой',
      // Салаты
      'японские салаты ростов',
      'салат чука ростов',
      'чука с доставкой',
      // Соусы / дополнения
      'соевый соус доставка',
      'имбирь васаби доставка',
      'палочки для суши',
      // Десерты
      'моти ростов',
      'моти с доставкой',
      'японские десерты ростов',

      // ═══════════════════════════════════════════════════════════════
      // 6. ЯПОНСКАЯ КУХНЯ — широкие запросы
      // ═══════════════════════════════════════════════════════════════
      'японская кухня ростов',
      'японская кухня с доставкой',
      'японская кухня ростов на дону',
      'японская еда ростов',
      'японская еда с доставкой',
      'японская еда ростов на дону',
      'японский ресторан ростов',
      'японский ресторан ростов на дону',
      'суши бар ростов',
      'суши бар ростов на дону',
      'суши бар доставка',
      'суши бар с доставкой ростов',
      'суши ресторан ростов',
      'азиатская кухня ростов',
      'азиатская кухня с доставкой',
      'азиатская еда ростов',
      'азиатская еда с доставкой ростов',
      'паназиатская кухня ростов',

      // ═══════════════════════════════════════════════════════════════
      // 7. ДОСТАВКА ЕДЫ — широкие (конкуренция с агрегаторами)
      // ═══════════════════════════════════════════════════════════════
      'доставка еды ростов',
      'доставка еды ростов на дону',
      'еда на дом ростов',
      'заказать еду ростов на дону',
      'заказать еду на дом ростов',
      'доставка еды на дом',
      'быстрая доставка еды ростов',
      'доставка еды рнд',
      'доставка обедов ростов',
      'доставка ужина ростов',
      'еда с доставкой ростов',
      'заказ еды онлайн ростов',
      'доставка пиццы и роллов ростов',

      // ═══════════════════════════════════════════════════════════════
      // 8. ЦЕНОВЫЕ / ВЫГОДНЫЕ
      // ═══════════════════════════════════════════════════════════════
      'недорогие роллы ростов',
      'недорогие суши ростов',
      'роллы недорого с доставкой',
      'дешёвые роллы ростов',
      'дешёвые суши ростов',
      'вкусные роллы ростов на дону',
      'вкусные суши ростов',
      'вкусные роллы ростов',
      'роллы акции ростов',
      'суши акции ростов',
      'роллы скидки',
      'суши скидки',
      'роллы промокод',
      'суши промокод',
      'роллы дёшево ростов',
      'суши дёшево ростов',
      'роллы по акции ростов',
      'суши по акции ростов',
      'бюджетные роллы ростов',
      'роллы от 99 рублей',
      'роллы от 149 рублей',
      'роллы выгодно',
      'суши выгодно',
      'роллы доставка на дом ростов',
      'суши доставка на дом ростов',
      'доставка роллов ростов на дом',
      'роллы кэшбэк',
      'роллы бонусы',
      'программа лояльности роллы',

      // ═══════════════════════════════════════════════════════════════
      // 9. СИТУАТИВНЫЕ / ИНФОРМАЦИОННЫЕ
      // ═══════════════════════════════════════════════════════════════
      'где заказать роллы в ростове',
      'где заказать суши в ростове',
      'лучшие роллы ростов',
      'лучшие суши ростов',
      'лучшая доставка роллов ростов',
      'лучшая доставка суши ростов',
      'рейтинг доставки суши ростов',
      'рейтинг доставки роллов ростов',
      'топ доставка роллов ростов',
      'топ доставка суши ростов',
      'доставка роллов отзывы ростов',
      'доставка суши отзывы ростов',
      'роллы на день рождения',
      'суши на день рождения',
      'роллы на праздник',
      'суши на праздник',
      'роллы на компанию',
      'суши на компанию',
      'роллы на вечеринку',
      'суши на вечеринку',
      'роллы на корпоратив',
      'суши на корпоратив',
      'роллы в офис ростов',
      'суши в офис ростов',
      'доставка роллов в офис ростов',
      'доставка суши в офис ростов',
      'роллы на свидание',
      'суши на двоих',
      'романтический ужин суши',
      'роллы на новый год',
      'роллы на 8 марта',
      'роллы на 14 февраля',
      'роллы на выходные',
      'суши на выходные',
      'что заказать на ужин ростов',
      'что заказать на обед ростов',
      'доставка роллов круглосуточно',
      'доставка суши ночью ростов',
      'доставка роллов сегодня',
      'доставка суши сегодня',
      'роллы сейчас ростов',
      'суши сейчас ростов',
      'быстрая доставка роллов ростов',
      'быстрая доставка суши ростов',
      'доставка роллов за 30 минут',
      'доставка роллов за час',
      'срочная доставка роллов ростов',

      // ═══════════════════════════════════════════════════════════════
      // 10. РАЙОНЫ РОСТОВА (generateKeywords не дублирует)
      // ═══════════════════════════════════════════════════════════════
      'доставка роллов ворошиловский район',
      'доставка роллов ленинский район',
      'доставка роллов советский район',
      'доставка роллов первомайский район',
      'доставка роллов октябрьский район',
      'доставка роллов пролетарский район',
      'доставка роллов кировский район',
      'доставка роллов железнодорожный район',
      'суши ворошиловский район ростов',
      'суши ленинский район ростов',
      'роллы западный ростов',
      'роллы северный ростов',
      'роллы левенцовка ростов',
      'роллы суворовский ростов',
      'роллы сельмаш ростов',
      'роллы темерник ростов',
      'роллы александровка ростов',
      'роллы батайск',
      'доставка роллов батайск',
      'роллы аксай',
      'доставка роллов аксай',

      // ═══════════════════════════════════════════════════════════════
      // 11. КОНКУРЕНТНЫЕ — названия конкурентов (без бренда, по типу)
      // ═══════════════════════════════════════════════════════════════
      'доставка роллов как суши вёсла',
      'альтернатива суши мастер ростов',
      'доставка роллов как тануки',
      'доставка суши как якитория',
      'роллы вместо пиццы ростов',

      // ═══════════════════════════════════════════════════════════════
      // 12. СПОСОБЫ ЗАКАЗА / ОПЛАТЫ
      // ═══════════════════════════════════════════════════════════════
      'заказать роллы по телефону ростов',
      'заказать суши по телефону ростов',
      'заказать роллы через сайт',
      'заказать суши через приложение',
      'роллы оплата картой',
      'суши оплата онлайн',
      // Ключ «роллы оплата при получении» убран 16.08.2026: наличных и оплаты
      // курьеру нет (вопрос 47), а этот ключ приводил из поиска ровно тех, кто
      // ищет оплату при получении — они доходят до корзины и упираются в
      // единственную онлайн-оплату картой. Остальные ключи этого блока
      // («оплата картой», «оплата онлайн») описывают то, что реально есть.
      'роллы самовывоз ростов',
      'суши самовывоз ростов',
      'роллы с собой ростов',
      'суши с собой ростов',
      'роллы навынос ростов',
      'суши навынос ростов',

      // ═══════════════════════════════════════════════════════════════
      // 13. СЕЗОННЫЕ / ТРЕНДОВЫЕ
      // ═══════════════════════════════════════════════════════════════
      'роллы меню 2025',
      'суши меню 2025',
      'новинки роллов ростов',
      'новинки суши ростов',
      'новое меню роллов',
      'сезонное меню суши',
      'хиты роллов ростов',
      'популярные роллы ростов',
      'популярные суши ростов',
      'самые вкусные роллы ростов',
      'самые популярные роллы ростов',
    ],
  },

  minOrderAmount: 1000,
  deliveryTime: {
    min: 45,
    max: 80,
  },
} as const;

export type SiteConfig = typeof siteConfig;
